<template>
  <div class="edit-comment-page">
    <div class="card">
      <h2 class="page-title">Edit comment</h2>
      <p class="page-lead">Update what you wrote. Only you can edit your own comments.</p>

      <div v-if="!comment && loading" class="muted">Loading comment…</div>
      <div v-else-if="!comment && !loading" class="muted error-text">Could not load this comment.</div>

      <template v-else>
        <div class="field">
          <label class="field-label">Author</label>
          <div class="field-value">{{ comment.name || 'Unknown' }}</div>
        </div>
        <div class="field">
          <label class="field-label" for="comment-body">Comment</label>
          <textarea
            id="comment-body"
            v-model="editContent"
            rows="6"
            class="textarea"
            placeholder="Your comment…"
          />
          <div class="char-hint">{{ editContent.length }} characters</div>
        </div>
        <div class="actions">
          <button type="button" class="btn primary" :disabled="saving" @click="saveComment">
            {{ saving ? 'Saving…' : 'Save changes' }}
          </button>
          <button type="button" class="btn ghost" :disabled="saving" @click="goBack">Cancel</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

function apiMessage(res) {
  const d = res && res.data;
  if (!d) return "";
  return d.msg || d.message || "";
}

export default {
  data() {
    return {
      comment: null,
      editContent: "",
      commentId: null,
      loading: true,
      saving: false,
    };
  },
  mounted() {
    const commentId = this.$route.query.id;
    this.commentId = commentId;
    if (commentId) {
      this.getComment(commentId);
    } else {
      this.loading = false;
      console.error("No commentId found in route");
    }
  },
  methods: {
    getComment(commentId) {
      this.loading = true;
      axios
        .get("/api/review/getComment", {
          params: { commentId },
        })
        .then((res) => {
          const d = res.data;
          this.comment = d && d.data !== undefined ? d.data : d;
          this.editContent = this.comment ? (this.comment.content || "") : "";
        })
        .catch((err) => {
          console.error("Failed to fetch Comment:", err);
          this.comment = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveComment() {
      if (!this.editContent.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Empty content",
          text: "Comment cannot be empty.",
          confirmButtonColor: "#3bc2ff",
        });
        return;
      }
      this.saving = true;
      axios
        .post("/api/review/editComment", {
          commentId: this.commentId,
          content: this.editContent.trim(),
        })
        .then((res) => {
          const body = res.data || {};
          if (body.code !== undefined && body.code !== 1) {
            Swal.fire({
              icon: "error",
              title: "Could not save",
              text: body.msg || "Update failed.",
              confirmButtonColor: "#3bc2ff",
            });
            return;
          }
          Swal.fire({
            icon: "success",
            title: "Saved",
            text: "Your comment was updated.",
            confirmButtonColor: "#3bc2ff",
            timer: 1800,
            showConfirmButton: true,
          }).then(() => {
            this.$router.go(-1);
          });
        })
        .catch((err) => {
          console.error("Failed to save comment:", err);
          const msg =
            apiMessage(err.response) ||
            (err.response && err.response.status === 404
              ? "Save endpoint missing or comment not found."
              : "Failed to save comment. Please try again.");
          Swal.fire({ icon: "error", title: "Error", text: msg, confirmButtonColor: "#3bc2ff" });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/");
      }
    },
  },
};
</script>

<style scoped>
.edit-comment-page {
  min-height: 60vh;
  padding: 24px 16px 48px;
  background: linear-gradient(180deg, rgba(59, 194, 255, 0.12) 0%, #f8fafc 45%);
}

.card {
  max-width: 560px;
  margin: 0 auto;
  background: #fff;
  border-radius: 20px;
  padding: 28px 28px 32px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
}

.page-title {
  margin: 0 0 8px;
  font-size: 1.6rem;
  color: #0f172a;
  font-weight: 700;
}

.page-lead {
  margin: 0 0 24px;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
}

.field {
  margin-bottom: 18px;
}

.field-label {
  display: block;
  font-weight: 600;
  font-size: 0.85rem;
  color: #334155;
  margin-bottom: 6px;
}

.field-value {
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 10px;
  color: #1e293b;
  font-size: 0.95rem;
}

.textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  font-size: 15px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.15s ease;
}

.textarea:focus {
  outline: none;
  border-color: #3bc2ff;
}

.char-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #94a3b8;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.btn {
  padding: 11px 22px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-size: 0.95rem;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn.primary {
  background: linear-gradient(135deg, #3bc2ff, #09d7d7);
  color: #fff;
  box-shadow: 0 6px 18px rgba(59, 194, 255, 0.35);
}

.btn.ghost {
  background: #fff;
  border: 2px solid #e2e8f0;
  color: #475569;
}

.muted {
  color: #94a3b8;
}

.error-text {
  color: #b91c1c;
}
</style>
